import {
  isValidElement,
  useState,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { Check, Copy } from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

const components: Components = {
  a: ({ children, href, ...props }) => {
    const external = Boolean(href && /^https?:\/\//i.test(href));
    return (
      <a
        {...props}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer noopener" : undefined}
        className="font-medium text-[#315d54] underline decoration-[#9ab5ae] underline-offset-2 hover:decoration-[#315d54]"
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-2 border-[#b8cbc6] pl-3 text-[#5e625b] first:mt-0 last:mb-0">
      {children}
    </blockquote>
  ),
  code: ({ children, className, ...props }) => (
    <code
      {...props}
      className={`${className ?? ""} rounded bg-[#eceeea] px-1.5 py-0.5 font-mono text-[0.82em] text-[#20272b]`}
    >
      {children}
    </code>
  ),
  h1: ({ children }) => (
    <h1 className="mb-2 mt-5 text-lg font-semibold leading-7 text-[#252622] first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-5 text-base font-semibold leading-6 text-[#252622] first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1.5 mt-4 font-semibold text-[#252622] first:mt-0">
      {children}
    </h3>
  ),
  hr: () => <hr className="my-4 border-[#dfe3dd]" />,
  ol: ({ children }) => (
    <ol className="my-3 ml-5 list-decimal space-y-1 first:mt-0 last:mb-0">
      {children}
    </ol>
  ),
  p: ({ children }) => (
    <p className="my-3 first:mt-0 last:mb-0">{children}</p>
  ),
  pre: ({ children }) => <MarkdownCodeBlock>{children}</MarkdownCodeBlock>,
  table: ({ children }) => (
    <div className="my-3 max-w-full overflow-x-auto rounded-lg border border-[#dfe3dd] first:mt-0 last:mb-0">
      <table className="w-full border-collapse text-left text-xs">
        {children}
      </table>
    </div>
  ),
  td: ({ children }) => (
    <td className="border-t border-[#dfe3dd] px-3 py-2 align-top">
      {children}
    </td>
  ),
  th: ({ children }) => (
    <th className="bg-[#f3f5f1] px-3 py-2 font-semibold text-[#374c47]">
      {children}
    </th>
  ),
  ul: ({ children }) => (
    <ul className="my-3 ml-5 list-disc space-y-1 first:mt-0 last:mb-0">
      {children}
    </ul>
  ),
};

export function RichMessage({ children }: { children: string }): ReactElement {
  return (
    <div className="min-w-0 overflow-hidden break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
        disallowedElements={["img"]}
        skipHtml
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

function MarkdownCodeBlock({
  children,
}: Pick<ComponentPropsWithoutRef<"pre">, "children">): ReactElement {
  const [copied, setCopied] = useState(false);
  const child = isValidElement<{ className?: string; children?: ReactNode }>(
    children,
  )
    ? children
    : null;
  const code = textContent(child?.props.children ?? children).replace(/\n$/, "");
  const language = child?.props.className?.match(/language-([^\s]+)/)?.[1];

  async function copyCode(): Promise<void> {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      // Clipboard access can be unavailable in privacy-focused browsers.
    }
  }

  return (
    <div className="my-3 overflow-hidden rounded-lg border border-[#d8ddd7] bg-[#f3f4f1] first:mt-0 last:mb-0">
      <div className="flex min-h-9 items-center justify-between border-b border-[#d8ddd7] px-3 text-[11px] text-[#6d7169]">
        <span className="font-mono">{language ?? "code"}</span>
        <button
          type="button"
          onClick={() => void copyCode()}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-1 font-medium text-[#4d6f67] hover:bg-[#e5e9e4]"
          aria-label={copied ? "Code copied" : "Copy code"}
        >
          {copied ? (
            <Check className="size-3" aria-hidden="true" />
          ) : (
            <Copy className="size-3" aria-hidden="true" />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="max-w-full overflow-x-auto p-3 font-mono text-xs leading-5 text-[#25312e]">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function textContent(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) return node.map(textContent).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return textContent(node.props.children);
  }
  return "";
}
